import React from 'react';
import './CartVariantPicker.css';

// The variant picker for a SIDEBAR line — POS cart and booking checkout both.
// The online shop picks a variant on the product page before adding to a
// basket; at the till there is no product page, so the line lands in the
// sidebar unresolved and is resolved in place. Same option-pill language as
// the shop (ProductDetail) so the two read as one product.

export const productHasVariants = (p) =>
  !!(p && Array.isArray(p.variants) && p.variants.length && Array.isArray(p.options) && p.options.length);

/** "Stone / M" — the label shown on the line, receipts and the stocktake. */
export const variantLabel = (options, variant) =>
  !variant || !variant.optionValues ? ''
    : (options || []).map((o) => variant.optionValues[o.name]).filter(Boolean).join(' / ');

/** The variant matching a full set of choices, or null while incomplete. */
export const findVariant = (options, variants, selection) => {
  if (!options || !options.length) return null;
  if (!options.every((o) => selection && selection[o.name])) return null;
  return (variants || []).find((v) =>
    options.every((o) => v.optionValues && v.optionValues[o.name] === selection[o.name])
  ) || null;
};

export const variantStock = (v) => Math.max(0, parseInt(v && v.quantity, 10) || 0);

/** Stock left for this line, honouring trackStock (0 = not tracked = no cap). */
export const stockCapFor = (item) => {
  if (!item || Number(item.trackStock) !== 1) return null;
  if (item.variantId) return variantStock((item.variants || []).find((v) => String(v._id) === String(item.variantId)));
  return Math.max(0, parseInt(item.stockQty, 10) || 0);
};

/** A product line that has variants but hasn't picked one yet blocks payment. */
export const lineNeedsVariant = (item) =>
  !!(item && item.type === 'product' && productHasVariants(item) && !item.variantId);

export const cartNeedsVariant = (items) => (items || []).some(lineNeedsVariant);

export default function CartVariantPicker({ options, variants, selection, onChange, onResolved }) {
  const setValue = (name, value) => {
    const next = { ...(selection || {}), [name]: value };
    onChange(next);
    const v = findVariant(options, variants, next);
    if (v && onResolved) onResolved(v);
  };

  return (
    <div className="cvp">
      {(options || []).map((o) => (
        <div key={o.name} className="cvp-group">
          <div className="cvp-label">{o.name}</div>
          <div className="cvp-pills">
            {o.values.map((val) => {
              // A value is a dead end if no variant matches it alongside the
              // choices already made on the OTHER options.
              const reachable = (variants || []).some((vt) =>
                vt.optionValues && vt.optionValues[o.name] === val &&
                (options || []).every((other) =>
                  other.name === o.name || !selection || !selection[other.name] ||
                  vt.optionValues[other.name] === selection[other.name])
              );
              // Out of stock is shown and disabled, never hidden — a salon
              // needs to see that Stone/M is the one they have run out of.
              const soldOut = reachable && !(variants || []).some((vt) =>
                vt.optionValues && vt.optionValues[o.name] === val &&
                (options || []).every((other) =>
                  other.name === o.name || !selection || !selection[other.name] ||
                  vt.optionValues[other.name] === selection[other.name]) &&
                variantStock(vt) > 0
              );
              const active = selection && selection[o.name] === val;
              return (
                <button
                  type="button"
                  key={val}
                  disabled={!reachable}
                  className={`cvp-pill${active ? ' is-active' : ''}${!reachable ? ' is-unavailable' : ''}${soldOut ? ' is-soldout' : ''}`}
                  onClick={() => setValue(o.name, val)}
                >
                  {val}{soldOut ? <span className="cvp-pill-note">0</span> : null}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
